import { type ApolloClient } from '@apollo/client'
import { type NextPageWithLayout, type GetStaticPropsContext } from 'next'
import React from 'react'
import { CMS_EXPERT_PAGE_SLUGS, EXPERT_DETAIL_TAB_KEYS } from 'shared-constants'
import { prepareGetStaticProps as prepareGetCmsPageStaticProps } from '@public/CmsPage/prepareGetStaticProps'
import { getPublicLayout } from '@public/components/PublicLayout'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { GA4_AREAS } from 'providers/googleAnalytics'
import { CmsExpertPageBySlugDocument } from 'types/graphql-generated'
import { ExpertDetailTabWrapper } from '../ExpertDetailTabWrapper'
import { expertTabsGetStaticProps } from '../utils'
import { ShowroomTabContent } from './Content'
import { prepareGetExpertStaticProps } from './prepareGetExpertStaticProps'

const ShowroomTabPage: NextPageWithLayout<{
  slug: string
  expertId: number
}> = ({ slug, expertId }) => (
  <ExpertDetailTabWrapper
    slug={slug}
    expertId={expertId}
    currentTab={EXPERT_DETAIL_TAB_KEYS.SHOWROOM}
  >
    <ShowroomTabContent slug={slug} expertId={expertId} />
  </ExpertDetailTabWrapper>
)

const showroomTabSpecificGetStaticProps = async (
  context: GetStaticPropsContext,
  { expertId, breadcrumbs, slug },
  apolloClient: ApolloClient<any>
) => {
  const expertApolloClient = await prepareGetExpertStaticProps(
    context,
    { expertId },
    apolloClient
  )

  const cmsPageStaticProps = await prepareGetCmsPageStaticProps(
    context,
    {
      slug: CMS_EXPERT_PAGE_SLUGS.SHOWROOM(expertId),
      query: CmsExpertPageBySlugDocument,
    },
    expertApolloClient
  )

  return {
    ...cmsPageStaticProps,
    props: {
      ...cmsPageStaticProps.props,
      breadcrumbs,
      slug,
      expertId,
      key: `${context.params.slug}-showroom-tab-page`,
    },
  }
}

export function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export function getStaticProps(context: GetStaticPropsContext) {
  return expertTabsGetStaticProps(context, {
    currentTab: EXPERT_DETAIL_TAB_KEYS.SHOWROOM,
    callback: showroomTabSpecificGetStaticProps,
  })
}

const ExpertShowroomDetail = withGA4Provider({
  area: GA4_AREAS.SHOWROOM,
})(ShowroomTabPage)

ExpertShowroomDetail.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true })

export default ExpertShowroomDetail
